<script setup>
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import siteTreeModule from '../../store/modules/siteTree';

import SiteTreeNavigationMenu from '../../common/site-tree/SiteTreeNavigationMenu.vue';
import SiteTree from '../../common/site-tree/SiteTree.vue';
import Button from "../../common/Button.vue";

const props = defineProps({
    options: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['hide-modal']);

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const siteTreeId = 'siteTreeModal';
const originalPropagatedPageIds = ref([]);

// Unpack options for readability
const {
    showContent = (props.options.selectContentMode || false),
    pageContentLinkId,
    pageId,
    onPageSelect,
    onContentSelect,
    onSave,
    selectContentMode,
    propagatedContentId
} = props.options;

// --- Computed ---

/**
 * Checks if propagation selections have changed by comparing
 * sorted JSON strings of page IDs.
 */
const isSaveBtnDisabled = computed(() => {
    if (!store.state[siteTreeId]) return true;

    const pageIds = store.state[siteTreeId].propagationData
        .map(o => o.pageId)
        .sort();

    return JSON.stringify(originalPropagatedPageIds.value) === JSON.stringify(pageIds);
});

const pageContentLinkItem = computed(() => {
    if (pageContentLinkId) {
        return store.state.pageContentData.items.find(o => o.id === pageContentLinkId);
    }
    return null;
});

// --- Methods ---

const setSelectedVars = () => {
    if (pageId) {
        store.dispatch(`${siteTreeId}/setPageId`, pageId);
        store.dispatch(`${siteTreeId}/setExpandedPageIds`, pageId);
    } else if (pageContentLinkId && pageContentLinkItem.value) {
        store.dispatch(`${siteTreeId}/setContentId`, pageContentLinkId);
        const pid = pageContentLinkItem.value.pageId;
        store.commit(`${siteTreeId}/addExpandedPageId`, pid);
        store.dispatch(`${siteTreeId}/setExpandedPageIds`, pid);
    }
};

const saveSelectedPageIds = () => {
    const propagationData = store.state[siteTreeId].propagationData;
    if (onSave) {
        onSave({ propagationData });
    }
    emit('hide-modal');
};

// --- Lifecycle ---

onMounted(async () => {
    // 1. Dynamic Vuex Module Registration
    if (store.hasModule(siteTreeId)) {
        store.unregisterModule(siteTreeId);
    }
    store.registerModule(siteTreeId, siteTreeModule);

    // 2. Initialize the module logic
    await store.dispatch(`${siteTreeId}/init`, {
        selectContentMode,
        showContent,
        propagatedContentId,
        onPageSelect: (pid) => {
            if (onPageSelect) {
                const bool = onPageSelect(pid);
                if (bool || bool === undefined) emit('hide-modal');
            }
        },
        onContentSelect: (pcid) => {
            if (onContentSelect) {
                const bool = onContentSelect(pcid);
                if (bool || bool === undefined) emit('hide-modal');
            }
        }
    });

    // 3. Set initial UI state (expanding the tree to current page)
    setSelectedVars();

    // 4. Handle Propagation Initial State
    if (propagatedContentId) {
        originalPropagatedPageIds.value = store.state[siteTreeId].propagationData
            .map(o => o.pageId)
            .sort();

        await store.dispatch('pageTemplatesContentAreas/init');
    }
});

onBeforeUnmount(() => {
    // Cleanup dynamic module to prevent memory leaks and state pollution
    if (store.hasModule(siteTreeId)) {
        store.unregisterModule(siteTreeId);
    }
});
</script>

<template>
    <div class="modal-dialog modal-lg site-tree-modal">
        <div class="modal-content">
            <div class="modal-header">
                <SiteTreeNavigationMenu
                    :site-tree-id="siteTreeId"
                    class="w-auto"
                />

                <button
                    aria-label="Close"
                    class="btn-close"
                    type="button"
                    @click="$emit('hide-modal')"
                ></button>
            </div>

            <div class="modal-body">
                <SiteTree :site-tree-id="siteTreeId"/>
            </div>

            <div v-if="propagatedContentId" class="modal-footer">
                <form @submit.prevent="saveSelectedPageIds">
                    <Button
                        type="submit"
                        class="ms-1 border"
                        :disabled="isSaveBtnDisabled"
                        :active-class="'btn-warning'"
                        :active-value="!isSaveBtnDisabled"
                    >
                        Save
                    </Button>
                </form>
            </div>
        </div>
    </div>
</template>